import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon, SearchIcon, Trash2Icon, Loader2Icon, BookOpenIcon, TagIcon, ChevronRightIcon, MailIcon } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { emailTemplateLibraryApi, EmailLibraryRecord } from '../services/creatorApi';
import { toast } from 'sonner';
export function EmailTemplatesLibrary() {
  const navigate = useNavigate();
  const {
    currentWorkspace
  } = useWorkspace();
  const [templates, setTemplates] = useState<EmailLibraryRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  useEffect(() => {
    loadTemplates();
  }, [currentWorkspace.name]);
  const loadTemplates = async () => {
    setIsLoading(true);
    try {
      const all = await emailTemplateLibraryApi.list();
      const filtered = all.filter(t => !t.Team || t.Team === currentWorkspace.name);
      setTemplates(filtered);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to load email templates', {
        description: msg
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    try {
      await emailTemplateLibraryApi.delete(id);
      setTemplates(prev => prev.filter(t => t.ID !== id));
      toast.success('Email template deleted');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to delete template', {
        description: msg
      });
    } finally {
      setIsDeleting(false);
      setDeleteConfirm(null);
    }
  };
  const filtered = templates.filter(t => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (t.Template_Name || '').toLowerCase().includes(q) || (t.Description || '').toLowerCase().includes(q) || (t.Email_Subject || '').toLowerCase().includes(q) || (t.Category || '').toLowerCase().includes(q);
  });
  if (isLoading) {
    return <div className="flex items-center justify-center py-24" data-id="element-4099">
        <Loader2Icon className="h-8 w-8 text-blue-500 animate-spin" data-id="element-4100" />
        <span className="ml-3 text-sm text-gray-500" data-id="element-4101">
          Loading email templates...
        </span>
      </div>;
  }
  return <div className="space-y-6" data-id="element-4102">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-4103">
        <div data-id="element-4104">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4105">
            Email Templates Library
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-4106">
            Reusable email templates you can use when linking forms to
            confirmation emails.
          </p>
        </div>
        <button onClick={() => navigate('/email-templates-library/new')} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap flex-shrink-0" data-id="element-4107">
          <PlusIcon className="h-4 w-4" data-id="element-4108" />
          New Template
        </button>
      </div>

      {templates.length > 0 && <div className="flex items-center gap-4 bg-white border border-gray-200 rounded-lg p-4" data-id="element-4109">
          <div className="relative flex-1 min-w-0" data-id="element-4110">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-4111" />
            <input type="text" placeholder="Search templates..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 pr-4 py-2 w-full border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" data-id="element-4112" />
          </div>
          <span className="text-sm text-gray-500 flex-shrink-0" data-id="element-4113">
            {filtered.length} template{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>}

      {filtered.length === 0 ? <div className="bg-white border border-gray-200 rounded-lg p-12 text-center" data-id="element-4114">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4" data-id="element-4115">
            <BookOpenIcon className="h-8 w-8 text-gray-400" data-id="element-4116" />
          </div>
          <h3 className="text-sm font-semibold text-gray-900" data-id="element-4117">
            {searchQuery ? 'No matching templates' : 'No email templates yet'}
          </h3>
          <p className="mt-1 text-sm text-gray-500 max-w-sm mx-auto" data-id="element-4118">
            {searchQuery ? 'Try adjusting your search.' : 'Create reusable email templates for confirmation emails, welcome messages, and more.'}
          </p>
          {!searchQuery && <button onClick={() => navigate('/email-templates-library/new')} className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-4119">
              <PlusIcon className="h-4 w-4" data-id="element-4120" />
              Create First Template
            </button>}
        </div> : <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-4121">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-4122">
            <thead className="bg-gray-50" data-id="element-4123">
              <tr data-id="element-4124">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4125">
                  Template
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4126">
                  Subject
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4127">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4128">
                  Mode
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4129">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200" data-id="element-4130">
              {filtered.map(tpl => <tr key={tpl.ID} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/email-templates-library/${tpl.ID}`)} data-id="element-4131">
                  <td className="px-6 py-4" data-id="element-4132">
                    <div className="flex items-center gap-3" data-id="element-4133">
                      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center" data-id="element-4134">
                        <MailIcon className="h-4 w-4 text-amber-600" data-id="element-4135" />
                      </div>
                      <div className="min-w-0" data-id="element-4136">
                        <p className="text-sm font-medium text-gray-900 truncate" data-id="element-4137">
                          {tpl.Template_Name || 'Untitled'}
                        </p>
                        {tpl.Description && <p className="text-xs text-gray-500 truncate max-w-xs" data-id="element-4138">
                            {tpl.Description}
                          </p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-4139">
                    <span className="text-sm text-gray-600 truncate block max-w-[200px]" data-id="element-4140">
                      {tpl.Email_Subject || <span className="text-gray-400" data-id="element-4141">—</span>}
                    </span>
                  </td>
                  <td className="px-6 py-4" data-id="element-4142">
                    {tpl.Category ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700" data-id="element-4143">
                        <TagIcon className="h-3 w-3" data-id="element-4144" />
                        {tpl.Category}
                      </span> : <span className="text-xs text-gray-400" data-id="element-4145">—</span>}
                  </td>
                  <td className="px-6 py-4" data-id="element-4146">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${tpl.Builder_Mode === 'builder' ? 'bg-purple-50 text-purple-700' : 'bg-gray-100 text-gray-600'}`} data-id="element-4147">
                      {tpl.Builder_Mode === 'builder' ? 'Builder' : 'Code'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-4148">
                    <div className="flex items-center justify-end gap-2" data-id="element-4149">
                      <button onClick={e => {
                  e.stopPropagation();
                  setDeleteConfirm(tpl.ID);
                }} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete" data-id="element-4150">
                        <Trash2Icon className="h-4 w-4" data-id="element-4151" />
                      </button>
                      <ChevronRightIcon className="h-4 w-4 text-gray-300" data-id="element-4152" />
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>}

      {deleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-4153">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-4154">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setDeleteConfirm(null)} data-id="element-4155" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-4156">
              <div className="flex items-center gap-4" data-id="element-4157">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-4158">
                  <Trash2Icon className="h-6 w-6 text-red-600" data-id="element-4159" />
                </div>
                <div data-id="element-4160">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-4161">
                    Delete Template
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-4162">
                    Are you sure? This cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6" data-id="element-4163">
                <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-4164">
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteConfirm)} disabled={isDeleting} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50" data-id="element-4165">
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}